import React, { FC, useCallback } from 'react';

import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FiberNewIcon from '@mui/icons-material/FiberNew';
import HandshakeIcon from '@mui/icons-material/Handshake';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';

import { ExtendedLead } from '../../../services/types/lead';
import LeadCard from './LeadCard';
import { LeadCardSkeletonGroup } from './LeadCardSkeleton';
import VirtualizedKanbanColumn from './VirtualizedKanbanColumn';

const STATUS_CONFIG = {
  CREATED: {
    label: 'CREADO',
    icon: <FiberNewIcon />,
    color: '#FF6B6B',
  },
  HANDLED_BY_AI: {
    label: 'IA',
    icon: <SmartToyIcon />,
    color: '#4ECDC4',
  },
  ASSIGNED_TO_SALESMAN: {
    label: 'ASIGNADO',
    icon: <AssignmentIndIcon />,
    color: '#45B7D1',
  },
  PENDING_TO_BE_HANDLED: {
    label: 'PENDIENTE',
    icon: <AccessTimeIcon />,
    color: '#FFA726',
  },
  HANDLED_BY_SALESMAN: {
    label: 'MANEJANDO',
    icon: <HandshakeIcon />,
    color: '#66BB6A',
  },
  CLOSED: {
    label: 'CERRADO',
    icon: <CheckCircleIcon />,
    color: '#9E9E9E',
  },
} as const;

const KanbanContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  height: '100%',
  overflow: 'auto',
  padding: theme.spacing(1),
}));

interface KanbanBoardProps {
  leadsByStatus: Record<string, ExtendedLead[]>;
  statusCounts: Record<string, number>;
  paginationInfo: Record<
    string,
    {
      page: number;
      hasMore: boolean;
      totalCount: number;
    }
  >;
  loadingStates: Record<
    string,
    {
      isLoadingMore: boolean;
      isRefreshing: boolean;
    }
  >;
  isInitialLoading: boolean;
  onLoadMore: (status: string) => void;
  getSkeletonCount: (status: string) => number;
}

const KanbanBoard: FC<KanbanBoardProps> = ({
  leadsByStatus,
  statusCounts,
  paginationInfo,
  loadingStates,
  isInitialLoading,
  onLoadMore,
  getSkeletonCount,
}) => {
  const handleLeadClick = useCallback((clientId: number) => {
    if (clientId) {
      const url = `/clients/${clientId}`;
      window.open(url, '_blank');
    }
  }, []);

  /**
   * Renders a lead card component
   *
   * @param {ExtendedLead} lead - Lead data
   * @param {Function} onLeadClick - Click handler
   * @returns {React.ReactNode} Rendered lead card
   */
  const renderLeadCard = useCallback((lead: ExtendedLead, onLeadClick: (clientId: number) => void) => {
    return <LeadCard lead={lead} onLeadClick={onLeadClick} />;
  }, []);

  if (isInitialLoading) {
    return (
      <KanbanContainer>
        {Object.entries(STATUS_CONFIG).map(([status, config]) => (
          <Box
            key={status}
            sx={{
              flex: '1 1 300px',
              minWidth: '280px',
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: 'background.default',
              borderRadius: 1,
              border: 1,
              borderColor: 'divider',
            }}
          >
            {/* Header */}
            <Box
              sx={{
                p: 2,
                backgroundColor: config.color,
                color: 'white',
                borderRadius: '4px 4px 0 0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography variant="h6">
                {config.icon} {config.label}
              </Typography>
              <CircularProgress size={20} sx={{ color: 'rgba(255,255,255,0.8)' }} />
            </Box>

            {/* Skeleton content */}
            <Box sx={{ p: 1, flex: 1 }}>
              <LeadCardSkeletonGroup count={6} delay={100} />
            </Box>
          </Box>
        ))}
      </KanbanContainer>
    );
  }

  return (
    <KanbanContainer>
      {Object.entries(STATUS_CONFIG).map(([status, config]) => {
        const leads = leadsByStatus[status] || [];
        const hasStatusCounts = typeof statusCounts[status] !== 'undefined';
        const totalCount = hasStatusCounts ? statusCounts[status] : 0;
        const pagination = paginationInfo[status] || { page: 0, hasMore: false, totalCount: 0 };
        const loading = loadingStates[status] || { isLoadingMore: false, isRefreshing: false };

        return (
          <VirtualizedKanbanColumn
            key={status}
            config={config}
            leads={leads}
            totalCount={totalCount}
            hasMore={pagination.hasMore}
            isLoadingMore={loading.isLoadingMore}
            isRefreshing={loading.isRefreshing}
            skeletonCount={getSkeletonCount(status)}
            onLeadClick={handleLeadClick}
            onLoadMore={() => onLoadMore(status)}
            renderLeadCard={renderLeadCard}
            statusCountLoading={typeof statusCounts[status] === 'undefined'}
          />
        );
      })}
    </KanbanContainer>
  );
};

export default KanbanBoard;
